import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { 
  Target, TrendingUp, TrendingDown, Scale, Dumbbell, 
  Apple, Flame, Trophy, ArrowRight, Zap, RefreshCw,
  ChevronRight, AlertCircle, CheckCircle, Sparkles,
  UtensilsCrossed, ShoppingCart, Calendar, Droplets
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const WEIGHT_GOALS = [
  { id: 'gain_muscle', label: 'Gain Muscle', icon: Dumbbell, color: 'text-purple-400', description: 'Build lean muscle mass with controlled calorie surplus' },
  { id: 'gain_weight', label: 'Gain Weight', icon: TrendingUp, color: 'text-green-400', description: 'Increase overall body weight' },
  { id: 'lose_fat', label: 'Lose Fat', icon: Flame, color: 'text-orange-400', description: 'Lose fat while preserving muscle mass' },
  { id: 'lose_weight', label: 'Lose Weight', icon: TrendingDown, color: 'text-red-400', description: 'General weight loss' },
  { id: 'maintain', label: 'Maintain', icon: Scale, color: 'text-blue-400', description: 'Maintain current weight and fitness' },
  { id: 'recomp', label: 'Body Recomp', icon: Zap, color: 'text-amber-400', description: 'Lose fat and gain muscle simultaneously' },
];

const ACTIVITY_LEVELS = [
  { id: 'sedentary', label: 'Sedentary', description: 'Little or no exercise' },
  { id: 'light', label: 'Light', description: '1-2 days/week' },
  { id: 'moderate', label: 'Moderate', description: '3-5 days/week' },
  { id: 'active', label: 'Active', description: '6-7 days/week' },
  { id: 'very_active', label: 'Very Active', description: '2x per day training' },
];

const GoalsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [goalProfile, setGoalProfile] = useState(null);
  const [hasGoals, setHasGoals] = useState(false);
  
  // Form state
  const [selectedGoal, setSelectedGoal] = useState('maintain');
  const [currentWeight, setCurrentWeight] = useState('');
  const [targetWeight, setTargetWeight] = useState('');
  const [height, setHeight] = useState('');
  const [age, setAge] = useState('');
  const [gender, setGender] = useState('male');
  const [activityLevel, setActivityLevel] = useState('moderate');
  
  // Weight log
  const [newWeight, setNewWeight] = useState('');
  const [loggingWeight, setLoggingWeight] = useState(false);
  
  // Meal plan
  const [generatingMealPlan, setGeneratingMealPlan] = useState(false);
  const [mealPlan, setMealPlan] = useState(null);
  const [groceryList, setGroceryList] = useState(null);

  useEffect(() => {
    fetchGoalProfile();
    fetchMealPlan();
  }, []);

  const fetchGoalProfile = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/api/goals/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.has_goals) {
        setHasGoals(true);
        setGoalProfile(response.data);
        const profile = response.data.goal_profile;
        setSelectedGoal(profile.weight_goal);
        setCurrentWeight(profile.current_weight?.toString() || '');
        setTargetWeight(profile.target_weight?.toString() || '');
        setHeight(profile.height_inches?.toString() || '');
        setAge(profile.age?.toString() || '');
        setGender(profile.gender || 'male');
        setActivityLevel(profile.activity_level || 'moderate');
      }
    } catch (error) {
      console.error('Failed to fetch goals:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMealPlan = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const [planRes, groceryRes] = await Promise.all([
        axios.get(`${API}/api/family/meal-plan`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/family/grocery-list`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      if (planRes.data.has_plan) {
        setMealPlan(planRes.data.meal_plan);
      }
      if (groceryRes.data.has_list) {
        setGroceryList(groceryRes.data.grocery_list);
      }
    } catch (error) {
      console.error('Failed to fetch meal plan:', error);
    }
  };

  const handleGenerateMealPlan = async () => {
    if (!hasGoals) {
      toast.error('Set your goals first to generate a personalized meal plan');
      return;
    }
    
    setGeneratingMealPlan(true);
    toast.info('Generating your personalized meal plan... This may take a minute.');
    
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.post(`${API}/api/family/meal-plan/generate`, 
        { days: 7 },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 }
      );
      
      if (response.data.success) {
        setMealPlan({ meal_plan: response.data.meal_plan });
        toast.success('Meal plan generated! Grocery list has been auto-created.');
        fetchMealPlan();
      }
    } catch (error) {
      toast.error('Failed to generate meal plan. Please try again.');
    } finally {
      setGeneratingMealPlan(false);
    }
  };

  const handleSaveGoals = async () => {
    if (!currentWeight) {
      toast.error('Current weight is required');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.post(`${API}/api/goals/set`, {
        weight_goal: selectedGoal,
        current_weight: parseFloat(currentWeight),
        target_weight: targetWeight ? parseFloat(targetWeight) : null,
        height_inches: height ? parseFloat(height) : null,
        age: age ? parseInt(age) : null,
        gender,
        activity_level: activityLevel
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Goals saved! Your nutrition and workout recommendations will now be personalized.');
      setHasGoals(true);
      fetchGoalProfile();
    } catch (error) {
      toast.error('Failed to save goals');
    } finally {
      setSaving(false);
    }
  };

  const handleLogWeight = async () => {
    if (!newWeight) {
      toast.error('Enter your weight');
      return;
    }
    
    setLoggingWeight(true);
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.post(`${API}/api/goals/log-weight`, {
        weight: parseFloat(newWeight)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.target_reached) {
        toast.success('🎉 Congratulations! You reached your target weight!');
      } else {
        toast.success('Weight logged - targets recalculated');
      }
      setNewWeight('');
      fetchGoalProfile();
    } catch (error) {
      toast.error('Failed to log weight');
    } finally {
      setLoggingWeight(false);
    }
  };

  const selectedGoalConfig = WEIGHT_GOALS.find(g => g.id === selectedGoal);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 text-aura-primary animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 pb-24">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-heading font-bold text-white uppercase tracking-tight">
            Goal-Driven System
          </h1>
          <p className="text-aura-text-muted mt-1">
            Set your weight goal and watch everything adapt - nutrition, workouts, and meal plans
          </p>
        </div>

        {/* Current Progress (if goals set) */}
        {hasGoals && goalProfile && (
          <Card className="bg-gradient-to-br from-aura-primary/20 to-aura-secondary/20 border-aura-primary/30">
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2">
                    {selectedGoalConfig && <selectedGoalConfig.icon className={`w-5 h-5 ${selectedGoalConfig.color}`} />}
                    <h3 className="text-lg font-bold text-white capitalize">
                      {goalProfile.goal_profile?.weight_goal?.replace('_', ' ')}
                    </h3>
                  </div>
                  <p className="text-sm text-aura-text-muted mt-1">
                    {goalProfile.goal_profile?.calculated_targets?.description}
                  </p>
                </div>
                <Badge className="bg-aura-primary/20 text-aura-primary border-0">
                  Active Goal
                </Badge>
              </div>
              
              {/* Progress Bar */}
              {goalProfile.goal_profile?.target_weight && (
                <div className="mt-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-aura-text-muted">
                      Start: {goalProfile.goal_profile?.starting_weight} lbs
                    </span>
                    <span className="text-white font-medium">
                      Current: {goalProfile.current_weight} lbs
                    </span>
                    <span className="text-aura-text-muted">
                      Target: {goalProfile.goal_profile?.target_weight} lbs
                    </span>
                  </div>
                  <Progress 
                    value={goalProfile.progress?.percent_complete || 0} 
                    className="h-3 bg-white/10"
                  />
                  <div className="flex justify-between mt-2">
                    <span className="text-xs text-aura-text-muted">
                      {goalProfile.progress?.weight_changed > 0 ? '+' : ''}{goalProfile.progress?.weight_changed} lbs changed
                    </span>
                    <span className="text-xs text-aura-primary">
                      {goalProfile.progress?.percent_complete}% complete
                    </span>
                  </div>
                </div>
              )}
              
              {/* Projections */}
              {goalProfile.goal_profile?.calculated_targets?.projections && (
                <div className="grid grid-cols-2 gap-4 mt-4 p-3 rounded-lg bg-white/5">
                  <div>
                    <p className="text-xs text-aura-text-muted">Weekly Change</p>
                    <p className="text-white font-medium">
                      {goalProfile.goal_profile.calculated_targets.projections.weekly_change_lbs > 0 ? '+' : ''}
                      {goalProfile.goal_profile.calculated_targets.projections.weekly_change_lbs} lbs
                    </p>
                  </div>
                  {goalProfile.goal_profile.calculated_targets.projections.weeks_to_target && (
                    <div>
                      <p className="text-xs text-aura-text-muted">Estimated Time</p>
                      <p className="text-white font-medium">
                        {goalProfile.goal_profile.calculated_targets.projections.weeks_to_target} weeks
                      </p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Quick Weight Log */}
        {hasGoals && (
          <Card className="bg-aura-surface border-white/10">
            <CardHeader className="pb-3">
              <CardTitle className="text-white font-heading uppercase tracking-wider flex items-center gap-2">
                <Scale className="w-5 h-5 text-aura-primary" />
                Log Today's Weight
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-3">
                <Input
                  type="number"
                  placeholder="Weight in lbs"
                  value={newWeight}
                  onChange={(e) => setNewWeight(e.target.value)}
                  className="bg-aura-bg border-white/10 text-white"
                />
                <Button 
                  onClick={handleLogWeight}
                  disabled={loggingWeight}
                  className="bg-aura-primary hover:bg-aura-primary-hover"
                >
                  {loggingWeight ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Log Weight'}
                </Button>
              </div>
              <p className="text-xs text-aura-text-muted mt-2">
                Logging weight recalculates your targets automatically
              </p>
            </CardContent>
          </Card>
        )}

        {/* Calculated Targets (if goals set) */}
        {hasGoals && goalProfile?.goal_profile?.calculated_targets && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-aura-surface border-white/10">
              <CardContent className="p-4 text-center">
                <Flame className="w-6 h-6 text-orange-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">
                  {goalProfile.goal_profile.calculated_targets.daily_calories}
                </p>
                <p className="text-xs text-aura-text-muted">Daily Calories</p>
              </CardContent>
            </Card>
            <Card className="bg-aura-surface border-white/10">
              <CardContent className="p-4 text-center">
                <Dumbbell className="w-6 h-6 text-purple-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">
                  {goalProfile.goal_profile.calculated_targets.macros?.protein_grams}g
                </p>
                <p className="text-xs text-aura-text-muted">Protein Target</p>
              </CardContent>
            </Card>
            <Card className="bg-aura-surface border-white/10">
              <CardContent className="p-4 text-center">
                <Apple className="w-6 h-6 text-green-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">
                  {goalProfile.goal_profile.calculated_targets.macros?.carb_grams}g
                </p>
                <p className="text-xs text-aura-text-muted">Carbs Target</p>
              </CardContent>
            </Card>
            <Card className="bg-aura-surface border-white/10">
              <CardContent className="p-4 text-center">
                <Target className="w-6 h-6 text-amber-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">
                  {goalProfile.goal_profile.calculated_targets.macros?.fat_grams}g
                </p>
                <p className="text-xs text-aura-text-muted">Fat Target</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* How It Works (for new users) */}
        {!hasGoals && (
          <Card className="bg-gradient-to-br from-aura-primary/10 to-aura-secondary/10 border-aura-primary/20">
            <CardContent className="p-6">
              <div className="flex items-start gap-3">
                <Sparkles className="w-6 h-6 text-aura-primary flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-bold text-white">How Goal-Driven System Works</h3>
                  <div className="mt-3 space-y-2 text-sm text-aura-text-muted">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span>Set your weight goal (gain muscle, lose fat, etc.)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span>AI calculates your exact calorie and macro targets</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span>Workout recommendations adapt to your goal</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span>Meal suggestions hit your exact targets</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span>Log weight weekly - system recalculates automatically</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Goal Selection */}
        <Card className="bg-aura-surface border-white/10">
          <CardHeader>
            <CardTitle className="text-white font-heading uppercase tracking-wider">
              {hasGoals ? 'Update Your Goal' : 'Select Your Goal'}
            </CardTitle>
            <CardDescription className="text-aura-text-muted">
              Choose what you want to achieve
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {WEIGHT_GOALS.map((goal) => (
                <button
                  key={goal.id}
                  onClick={() => setSelectedGoal(goal.id)}
                  className={`p-4 rounded-lg border-2 transition-all text-left ${
                    selectedGoal === goal.id
                      ? 'border-aura-primary bg-aura-primary/10'
                      : 'border-white/10 bg-white/5 hover:border-white/20'
                  }`}
                >
                  <goal.icon className={`w-6 h-6 ${goal.color} mb-2`} />
                  <p className="text-white font-medium text-sm">{goal.label}</p>
                  <p className="text-xs text-aura-text-muted mt-1 line-clamp-2">{goal.description}</p>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Body Stats Form */}
        <Card className="bg-aura-surface border-white/10">
          <CardHeader>
            <CardTitle className="text-white font-heading uppercase tracking-wider">
              Your Stats
            </CardTitle>
            <CardDescription className="text-aura-text-muted">
              Used to calculate your personalized targets
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-aura-text-muted">Current Weight (lbs) *</Label>
                <Input
                  type="number"
                  value={currentWeight}
                  onChange={(e) => setCurrentWeight(e.target.value)}
                  placeholder="175"
                  className="bg-aura-bg border-white/10 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-aura-text-muted">Target Weight (lbs)</Label>
                <Input
                  type="number"
                  value={targetWeight}
                  onChange={(e) => setTargetWeight(e.target.value)}
                  placeholder="185"
                  className="bg-aura-bg border-white/10 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-aura-text-muted">Height (inches)</Label>
                <Input
                  type="number"
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  placeholder="72"
                  className="bg-aura-bg border-white/10 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-aura-text-muted">Age</Label>
                <Input
                  type="number"
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  placeholder="18"
                  className="bg-aura-bg border-white/10 text-white mt-1"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-aura-text-muted">Gender</Label>
                <Select value={gender} onValueChange={setGender}>
                  <SelectTrigger className="bg-aura-bg border-white/10 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-aura-surface border-white/10">
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-aura-text-muted">Activity Level</Label>
                <Select value={activityLevel} onValueChange={setActivityLevel}>
                  <SelectTrigger className="bg-aura-bg border-white/10 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-aura-surface border-white/10">
                    {ACTIVITY_LEVELS.map(level => (
                      <SelectItem key={level.id} value={level.id}>
                        {level.label} - {level.description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button 
              onClick={handleSaveGoals}
              disabled={saving}
              className="w-full bg-aura-primary hover:bg-aura-primary-hover mt-4"
            >
              {saving ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Calculating...</>
              ) : (
                <><Target className="w-4 h-4 mr-2" /> {hasGoals ? 'Update Goals' : 'Set Goals & Calculate Targets'}</>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Workout Recommendations Preview */}
        {hasGoals && goalProfile?.goal_profile?.calculated_targets?.workout_recommendations && (
          <Card className="bg-aura-surface border-white/10">
            <CardHeader>
              <CardTitle className="text-white font-heading uppercase tracking-wider flex items-center gap-2">
                <Dumbbell className="w-5 h-5 text-purple-400" />
                Workout Recommendations
              </CardTitle>
              <CardDescription className="text-aura-text-muted">
                Based on your {goalProfile.goal_profile.weight_goal?.replace('_', ' ')} goal
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 rounded-lg bg-white/5 text-center">
                  <p className="text-xs text-aura-text-muted uppercase">Volume</p>
                  <p className="text-white font-medium capitalize mt-1">
                    {goalProfile.goal_profile.calculated_targets.workout_recommendations.volume}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-white/5 text-center">
                  <p className="text-xs text-aura-text-muted uppercase">Focus</p>
                  <p className="text-white font-medium capitalize mt-1">
                    {goalProfile.goal_profile.calculated_targets.workout_recommendations.focus?.replace('_', ' ')}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-white/5 text-center">
                  <p className="text-xs text-aura-text-muted uppercase">Cardio</p>
                  <p className="text-white font-medium capitalize mt-1">
                    {goalProfile.goal_profile.calculated_targets.workout_recommendations.cardio}
                  </p>
                </div>
              </div>
              <Button 
                variant="outline" 
                className="w-full mt-4 border-white/10 text-white"
                onClick={() => navigate('/training-program')}
              >
                Get AI Workout Recommendation <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Meal Plan & Grocery List Section */}
        {hasGoals && (
          <Card className="bg-aura-surface border-white/10">
            <CardHeader>
              <CardTitle className="text-white font-heading uppercase tracking-wider flex items-center gap-2">
                <UtensilsCrossed className="w-5 h-5 text-green-400" />
                Personalized Meal Plan
              </CardTitle>
              <CardDescription className="text-aura-text-muted">
                AI-generated meal plan tailored to your exact macro targets
              </CardDescription>
            </CardHeader>
            <CardContent>
              {mealPlan ? (
                <div className="space-y-4">
                  <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      <div>
                        <p className="text-white font-medium">7-Day Meal Plan Active</p>
                        <p className="text-xs text-aura-text-muted">
                          Generated {mealPlan.generated_at ? new Date(mealPlan.generated_at).toLocaleDateString() : 'recently'}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <Button 
                      variant="outline" 
                      className="border-white/10 text-white"
                      onClick={() => navigate('/meal-plan')}
                    >
                      <Calendar className="w-4 h-4 mr-2" />
                      View Meal Plan
                    </Button>
                    <Button 
                      variant="outline" 
                      className="border-white/10 text-white"
                      onClick={() => navigate('/grocery-list')}
                    >
                      <ShoppingCart className="w-4 h-4 mr-2" />
                      Grocery List {groceryList && `(${groceryList.total_items})`}
                    </Button>
                  </div>
                  
                  <Button 
                    onClick={handleGenerateMealPlan}
                    disabled={generatingMealPlan}
                    variant="outline"
                    className="w-full border-green-500/30 text-green-400 hover:bg-green-500/10"
                  >
                    {generatingMealPlan ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                    ) : (
                      <><RefreshCw className="w-4 h-4 mr-2" /> Regenerate Meal Plan</>
                    )}
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="p-4 rounded-lg bg-white/5 border border-white/10 text-center">
                    <UtensilsCrossed className="w-10 h-10 text-aura-text-muted mx-auto mb-3" />
                    <p className="text-white font-medium">No Meal Plan Yet</p>
                    <p className="text-sm text-aura-text-muted mt-1">
                      Generate a personalized 7-day meal plan that hits your exact macro targets
                    </p>
                  </div>
                  
                  <Button 
                    onClick={handleGenerateMealPlan}
                    disabled={generatingMealPlan}
                    className="w-full bg-green-600 hover:bg-green-700"
                  >
                    {generatingMealPlan ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Creating Your Meal Plan...</>
                    ) : (
                      <><Sparkles className="w-4 h-4 mr-2" /> Generate My Meal Plan</>
                    )}
                  </Button>
                  
                  <p className="text-xs text-aura-text-muted text-center">
                    This will also auto-generate a grocery list for your family
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default GoalsPage;
